import React from 'react';
import { parseAddress, formatRelativeDate } from '../utils/emailParsing';

const SparkleIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2.5l1.85 5.15L19 9.5l-5.15 1.85L12 16.5l-1.85-5.15L5 9.5l5.15-1.85z" />
    <path d="M18.5 15l.85 2.15L21.5 18l-2.15.85L18.5 21l-.85-2.15L15.5 18l2.15-.85z" opacity="0.55" />
  </svg>
);

const previewOf = (text) => String(text || '').replace(/\s+/g, ' ').trim();

/**
 * AgentDraftsBanner — a strip above the mail panes listing the replies
 * Inbox Reply has already written.
 *
 * Renders nothing when there is nothing to review. Selecting a card opens the
 * original mail with the draft loaded into the composer, so the answer is
 * always read next to the message it belongs to rather than sent blind.
 */
export default function AgentDraftsBanner({
  drafts,
  expanded,
  onToggle,
  onOpenDraft,
  onDismissDraft,
  activeEmailId,
  bulkMode = false,
  bulkTotal = 0,
  bulkDone = 0,
  onToggleBulk,
  onExitBulk,
}) {
  const count = drafts.length;
  if (count === 0 && !bulkMode) return null;

  // Bulk Reply mode: compact progress rail replaces the cards + toggle.
  // Sits in the same slot so the layout below never shifts when the user
  // enters or leaves the mode.
  if (bulkMode) {
    const total = Math.max(bulkTotal, bulkDone + count);
    const done = Math.min(bulkDone, total);
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    const remaining = Math.max(total - done, 0);
    return (
      <div className="ivx-agent-banner bulk">
        <div className="ivx-agent-bar ivx-agent-bar-bulk">
          {/* The leading icon doubles as the exit control: a back arrow that
              returns the user to the normal two-pane inbox. Replaces the
              earlier separate "Exit" text button. */}
          <button
            type="button"
            className="ivx-agent-back"
            onClick={onExitBulk}
            title="Back to inbox"
            aria-label="Back to inbox"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="ivx-agent-lead">
            <strong className="ivx-agent-title">
              Bulk Reply — {done} of {total} done
            </strong>
            <span className="ivx-agent-sub">
              {remaining === 0
                ? 'All prepared replies handled — great job!'
                : `${remaining} to go — send or discard to jump to the next.`}
            </span>
          </span>
          <div className="ivx-agent-progress" aria-label={`${percent}% complete`}>
            <div className="ivx-agent-progress-fill" style={{ width: `${percent}%` }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`ivx-agent-banner ${expanded ? 'expanded' : ''}`}>
      <button
        type="button"
        className="ivx-agent-bar"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="ivx-agent-lead">
          <strong className="ivx-agent-title">
            {count} {count === 1 ? 'reply' : 'replies'} ready to review
          </strong>
          <span className="ivx-agent-sub">
            Written by Inbox Reply — open one to check it before sending.
          </span>
        </span>
        {onToggleBulk && count > 0 && (
          <span
            className="ivx-agent-bulk-btn"
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onToggleBulk(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                onToggleBulk();
              }
            }}
            title="Reply to all prepared drafts one after another"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M4 7h13M4 12h13M4 17h9M20 6v6M20 6l-2 2M20 6l2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Bulk Reply
          </span>
        )}
        <span className="ivx-agent-toggle">
          {expanded ? 'Hide' : 'Review'}
          <svg
            className={`ivx-agent-chevron ${expanded ? 'up' : ''}`}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {expanded && (
        <div className="ivx-agent-cards">
          {drafts.map((draft) => {
            const sender = parseAddress(draft.originalEmail?.from);
            const subject = draft.originalEmail?.subject || '(No subject)';
            const isActive = activeEmailId && activeEmailId === draft.originalEmailId;

            return (
              <div key={draft.id} className={`ivx-agent-card ${isActive ? 'active' : ''}`}>
                <button
                  type="button"
                  className="ivx-agent-card-main"
                  onClick={() => onOpenDraft(draft)}
                  title={`Open the email from ${sender.name || sender.email}`}
                >
                  <span className="ivx-agent-card-top">
                    <span className="ivx-agent-card-sender">
                      {sender.name || sender.email || 'Unknown'}
                    </span>
                    <span className="ivx-agent-card-time">{formatRelativeDate(draft.ts)}</span>
                  </span>
                  <span className="ivx-agent-card-subject">Re: {subject}</span>
                  <span className="ivx-agent-card-preview">{previewOf(draft.draftContent)}</span>
                </button>

                <button
                  type="button"
                  className="ivx-agent-card-dismiss"
                  onClick={() => onDismissDraft(draft)}
                  title="Discard this draft"
                  aria-label="Discard this draft"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * AgentDraftsHint — shown only when the user has defined cases but automatic
 * replying is switched off, i.e. the one situation where an empty banner is a
 * setting problem rather than simply "no replies yet".
 */
export function AgentDraftsHint({ onOpenSettings, onDismiss }) {
  return (
    <div className="ivx-agent-hint">
      <span className="ivx-agent-hint-icon">
        <SparkleIcon width="13" height="13" />
      </span>
      <span className="ivx-agent-hint-text">
        Your cases are set up, but Inbox Reply is switched off — turn it on under
        Intelligence → Inbox Reply to get written answers here.
      </span>
      {onOpenSettings && (
        <button type="button" className="ivx-agent-hint-link" onClick={onOpenSettings}>
          Open settings
        </button>
      )}
      <button
        type="button"
        className="ivx-agent-hint-close"
        onClick={onDismiss}
        title="Hide this notice"
        aria-label="Hide this notice"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
